import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_HANDLE, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { BaseTemplate } from "@/components/templates/BaseTemplate";

/**
 * Cali — Public SEO landing page using BaseTemplate.
 * /cali and /en/cali alternatives. Rich layout (hero video + gallery + Stripe CTAs).
 * Full admin control parity with Argentina (site-settings + heroCta + country-template for CO → Stripe).
 */
const Cali = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEn = location.pathname.startsWith("/en/") || location.pathname.startsWith("/en");

  useEffect(() => {
    if (isEn && i18n.language?.split("-")[0] !== "en") {
      void i18n.changeLanguage("en");
    }
  }, [isEn, i18n]);

  const locale = isEn ? "en_US" : "es_CO";
  const title = isEn
    ? `${BRAND_PERSON_NAME} | Cali Colombia — Content Creator | Exclusive Lifestyle Content`
    : `${BRAND_PERSON_NAME} | Cali Colombia — Creadora de Contenido | Contenido Exclusivo Lifestyle`;
  const description = isEn
    ? `Premium fitness, lifestyle & beauty content from ${BRAND_PERSON_NAME} for Cali and Colombia. Private membership powered by Stripe.`
    : `Contenido premium de fitness, lifestyle y belleza de ${BRAND_PERSON_NAME} para Cali y Colombia. Membresía privada con Stripe.`;
  const keywords = isEn
    ? `${BRAND_PERSON_NAME}, cali, colombia, content creator cali, fitness cali, exclusive membership, stripe, delfuocampo cali`
    : `${BRAND_PERSON_NAME}, cali, colombia, creadora cali, fitness cali, membresia exclusiva, stripe, delfuocampo cali`;

  useSEO({
    title,
    description,
    keywords,
    ogImage: `${BRAND_SITE_URL}/og.png`,
    ogTitle: title,
    ogDescription: description,
    canonical: `${BRAND_SITE_URL}${location.pathname.replace(/\/$/, "") || "/cali"}`,
    author: BRAND_PERSON_NAME,
    locale,
    alternateLocale: isEn ? "es_CO" : "en_US",
    type: "website",
    publishedTime: "2025-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
  });

  const heroTitle = isEn ? `${BRAND_PERSON_NAME} in Cali` : `${BRAND_PERSON_NAME} Cali`;
  const heroDescription = isEn
    ? "Exclusive private content for Cali & Valle. Join with Stripe."
    : "Contenido privado exclusivo para Cali y el Valle. Únete con Stripe.";

  return (
    <BaseTemplate
      heroTitle={heroTitle}
      heroDescription={heroDescription}
      showSobreMi={false}
      showPortfolioNavLink
      showGalleryNavLink
      showContactNavLink
      showGallerySection
      showTallySection
    />
  );
};

export default Cali;
